import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CircleNotch, Warning } from "@phosphor-icons/react";

/**
 * Direct Google OAuth 2.0 callback.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */

const ROLE_HOME = {
  admin: "/admin",
  school: "/school",
  parent: "/parent",
  agent: "/agent",
  credit_reviewer: "/approver",
};

export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Google sends `?code=...&state=...` (and `?error=...` on denial).
    const qs = new URLSearchParams(location.search);
    const code = qs.get("code");
    const state = qs.get("state");
    const errCode = qs.get("error");

    if (errCode) {
      setErr(`Google sign-in was cancelled (${errCode}). Please try again.`);
      return;
    }
    if (!code) {
      setErr("Google callback is missing an authorisation code. Please try signing in again.");
      return;
    }

    // CSRF check against the state we stored in sessionStorage before leaving.
    let storedState = null;
    try { storedState = sessionStorage.getItem("ar_oauth_state"); } catch { /* ignore */ }
    if (storedState && state && storedState !== state) {
      setErr("Sign-in security check failed. Please try again from the sign-in page.");
      return;
    }

    const redirectUri = window.location.origin + "/auth/callback";

    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", { code, redirect_uri: redirectUri });
        try {
          sessionStorage.removeItem("ar_oauth_state");
          sessionStorage.removeItem("ar_oauth_redirect_uri");
        } catch { /* ignore */ }
        await refresh();
        // Clear the query string so a refresh doesn't retry the (now-used) code.
        window.history.replaceState({}, document.title, "/auth/callback");
        const dest = ROLE_HOME[data.role] || "/dashboard";
        nav(dest, { replace: true, state: { user: data } });
      } catch (e) {
        setErr(formatErr(e?.response?.data?.detail) || e.message);
      }
    })();
  }, [location.search, refresh, nav]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#F9F7F4]">
      <div className="w-full max-w-md card !p-10 text-center" data-testid="auth-callback">
        {err ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#FBE3E3] grid place-items-center mx-auto">
              <Warning size={28} weight="fill" className="text-[#C93B3B]" />
            </div>
            <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Sign-in failed</h1>
            <p className="text-sm text-[#4F5D4F] mt-3" data-testid="auth-callback-error">{err}</p>
            <button
              onClick={() => nav("/login", { replace: true })}
              className="btn-primary mt-6"
              data-testid="auth-callback-retry"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-[#E2F3EB] grid place-items-center mx-auto">
              <CircleNotch size={28} className="text-[#0A3622] animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Signing you in…</h1>
            <p className="text-sm text-[#4F5D4F] mt-3">Verifying your Google identity with Ascentremit.</p>
          </>
        )}
      </div>
    </div>
  );
}
